import React, { useState, useRef } from 'react'
import cx from 'classnames'
import Icon from 'components/Icon'
import { useOnClickOutside } from 'hooks/click-outside'
import { useCssTransition } from 'hooks/transitioning'
import { useOnEscDown } from 'hooks/key-press'
import { useViewportSize } from 'hooks/viewport-hooks'
import { match } from 'utils/pattern-match-util'
import { mergeRefs } from 'utils/react-util'
import styles from './Dropdown.sass'
import { useElementSize } from 'hooks/element-size'
import {
  ModalSheet,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetContent,
} from 'components/ModalSheet'
import Button from 'components/Button'
import {
  Placement,
  useFloating,
  offset,
  autoUpdate,
  flip,
} from '@floating-ui/react'
import ReactDOM from 'react-dom'
import useForkRef from 'hooks/ref-hook'

export type RenderChildren = (actions: {
  toggle: () => unknown
}) => React.ReactNode

export interface DropdownButtonProps {
  ['data-test']?: string
  onClick: (e?: React.MouseEvent | React.TouchEvent) => unknown
}

export type RenderButton = (
  props: DropdownButtonProps,
  ref: React.Ref<HTMLElement | null>,
) => React.ReactNode

type Event = React.MouseEvent | React.TouchEvent

type DropdownWidth = 'default' | 'medium' | 'small' | 'miniscule'

interface DropdownProps {
  autoDismiss?: boolean
  button?: React.ReactNode | RenderButton
  isOpen?: boolean
  disabled?: boolean
  onButtonClick?: () => unknown
  onRequestClose?: () => unknown
  position?: Placement
  contentOverButton?: boolean
  allowOverflow?: boolean
  children: RenderChildren | React.ReactNode
  dataTest?: string
  minWidth?: number
  width?: DropdownWidth
  compact?: boolean
  actionCompact?: boolean
  alignContent?: string
  noBottomPadding?: boolean
}

function alignDropdownContent(align?: string) {
  switch (align) {
    case 'left':
      return styles.alignLeft
    case 'right':
      return styles.alignRight
    default:
      return ''
  }
}

// Dropdown
export const Dropdown = ({
  autoDismiss,
  button,
  children,
  isOpen,
  onButtonClick,
  onRequestClose,
  position = 'bottom-end',
  disabled,
  contentOverButton,
  allowOverflow,
  dataTest,
  minWidth,
  width = 'default',
  alignContent = 'center',
  noBottomPadding,
}: DropdownProps) => {
  const size = useViewportSize()
  const isModalSheet = size.width <= 500

  // Ref setup
  const [buttonEl, setButtonEl] = useState<HTMLElement | null>(null)
  const dropdownRef = useRef<HTMLElement | null>(null)
  const buttonRef = useRef<HTMLElement | null>(buttonEl)

  buttonRef.current = buttonEl

  // Internal 'isOpen' state
  const [internalIsOpen, setInternalIsOpen] = useState(false)

  // Determines if the dropdown is controlled.
  const isControlled = isOpen !== undefined

  const close = React.useCallback(() => {
    if (isControlled) {
      if (onRequestClose && isOpen) {
        onRequestClose()
      }
      return
    }

    if (autoDismiss) {
      setInternalIsOpen(false)
    }
  }, [autoDismiss, isOpen, onRequestClose, isControlled])

  const closeModalSheet = React.useCallback(() => {
    if (isControlled) {
      if (onRequestClose && isOpen) {
        onRequestClose()
      }
      return
    }

    setInternalIsOpen(false)
  }, [autoDismiss, isOpen, onRequestClose, isControlled])

  // If configured, dismisses the dropdown
  const onClickOutside = React.useCallback(
    (event: MouseEvent | TouchEvent) => {
      // On mobile we use the modal sheet
      if (isModalSheet) {
        return
      }

      if (
        !autoDismiss &&
        (!dropdownRef.current ||
          dropdownRef.current.contains(event.target as Node))
      ) {
        return
      }

      close()
    },
    [autoDismiss, close],
  )

  useOnEscDown(close)
  useOnClickOutside(buttonRef, onClickOutside)

  // Toggles 'internalIsOpen' state
  const toggleDropdown = React.useCallback(() => {
    setInternalIsOpen(!internalIsOpen)
  }, [internalIsOpen])

  // On button click, toggle the 'isOpen' state, or if controlled, trigger the callback.
  const onClickCatchall = React.useCallback(
    (event?: Event) => {
      event?.preventDefault()
      event?.stopPropagation()
      if (!isControlled) {
        toggleDropdown()
      } else if (onButtonClick) {
        onButtonClick()
      }
    },
    [toggleDropdown, onButtonClick, isControlled],
  )

  const dropdownIsOpen = !disabled && (!isControlled ? internalIsOpen : isOpen)

  const renderChildren = () =>
    typeof children === 'function'
      ? (children as RenderChildren)({
          toggle: toggleDropdown,
        })
      : children

  return (
    <div className={styles.dropdown}>
      {typeof button === 'function' ? (
        button({ onClick: onClickCatchall, 'data-test': dataTest }, setButtonEl)
      ) : (
        <div
          className={cx(
            styles.dropdownButtonContainer,
            alignDropdownContent(alignContent),
          )}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore: no contravariance in TS
          ref={setButtonEl}
          onClick={onClickCatchall}
          data-test={dataTest}
        >
          {button}
        </div>
      )}

      {isModalSheet ? (
        <ModalSheet showing={Boolean(dropdownIsOpen)} onRequestClose={close}>
          <ModalSheetContent>
            <ul className={styles.dropdownSheetContent}>{renderChildren()}</ul>
          </ModalSheetContent>

          <ModalSheetFooter>
            <ModalSheetFooterButtonGroup>
              <Button onClick={closeModalSheet} disabled={disabled}>
                Close
              </Button>
            </ModalSheetFooterButtonGroup>
          </ModalSheetFooter>
        </ModalSheet>
      ) : (
        <InlineDropdownContent
          renderChildren={renderChildren}
          dropdownIsOpen={dropdownIsOpen}
          allowOverflow={allowOverflow}
          contentOverButton={contentOverButton}
          position={position}
          buttonEl={buttonEl}
          dropdownRef={dropdownRef}
          minWidth={minWidth}
          width={width}
          noBottomPadding={noBottomPadding}
        />
      )}
    </div>
  )
}

interface InlineDropdownContentProps {
  dropdownIsOpen?: boolean
  renderChildren: () => React.ReactNode
  allowOverflow?: boolean
  contentOverButton?: boolean
  position?: DropdownProps['position']
  buttonEl?: HTMLElement | null
  dropdownRef: React.MutableRefObject<HTMLElement | null>
  minWidth?: number
  width?: DropdownWidth
  noBottomPadding?: boolean
}

const InlineDropdownContent = ({
  renderChildren,
  dropdownIsOpen,
  allowOverflow,
  contentOverButton,
  position,
  buttonEl,
  dropdownRef,
  minWidth,
  width = 'default',
  noBottomPadding,
}: InlineDropdownContentProps) => {
  const {
    refs,
    strategy,
    x: left,
    y: top,
    update,
  } = useFloating({
    strategy: 'absolute',
    placement: position,
    middleware: [flip(), offset(10)],
    whileElementsMounted: autoUpdate,
    elements: {
      reference: buttonEl,
    },
  })

  const floatingDropdownRef = useForkRef(dropdownRef, refs.setFloating)

  // Controls the refs for useFloating.
  React.useEffect(() => {
    if (dropdownIsOpen && refs.reference.current && refs.floating.current) {
      return autoUpdate(refs.reference.current, refs.floating.current, update, {
        animationFrame: true,
      })
    }
  }, [buttonEl, refs.setFloating, dropdownIsOpen])

  const isPositionTop = position?.includes('top')
  const [transitionRef, rendering, hidden] = useCssTransition(!!dropdownIsOpen)
  const mergedRef = mergeRefs([floatingDropdownRef, transitionRef])

  return ReactDOM.createPortal(
    <>
      {rendering && (
        <ul
          ref={mergedRef}
          className={cx(
            styles.dropdownContent,
            allowOverflow && styles.allowOverflow,
            contentOverButton && styles.contentOverButton,
            noBottomPadding && styles.noBottomPadding,
            position && position.split(' ').map((x) => styles[x]),
            isPositionTop ? styles.top : styles.bottom,
            hidden && styles.hidden,
            styles[width],
          )}
          style={{
            position: strategy,
            minWidth,
            top: top ?? 0,
            left: left ?? 0,
          }}
        >
          {renderChildren()}
        </ul>
      )}
    </>,
    document.getElementById('app-modals-container')!,
  )
}

export enum CheckboxState {
  Checked = 'checked',
  Unchecked = 'unchecked',
  Indeterminate = 'indeterminate',
}

interface ItemProps {
  active?: boolean
  checkboxState?: CheckboxState
  checkmark?: boolean
  relative?: boolean
  disabled?: boolean
  shortcut?: string
  draggable?: boolean
  isDragging?: boolean
  onClick?: any
  icon?: React.ReactNode
  children?: React.ReactNode
  role?: string
  tabIndex?: number
  onDragStart?: React.DragEventHandler<any>
  muteClickEvent?: boolean
  dataTest?: string
}

/**
 * A dropdown item.
 */
export const DropdownItem = React.forwardRef(
  (
    {
      active,
      checkboxState,
      checkmark,
      children,
      relative,
      disabled,
      icon,
      shortcut,
      draggable,
      isDragging,
      onClick,
      muteClickEvent = false,
      dataTest,
      ...other
    }: ItemProps,
    ref: React.Ref<HTMLLIElement>,
  ) => {
    const onClickCatchall = React.useCallback(
      (e?: Event) => {
        if (muteClickEvent) {
          e?.stopPropagation()
        }
        if (onClick) {
          onClick()
        }
      },
      [muteClickEvent, onClick],
    )

    return (
      <li className={styles.dropdownItem} ref={ref} {...other}>
        <button
          className={cx(
            styles.dropdownItemContent,
            active && styles.active,
            disabled && !checkboxState && styles.disabled,
            relative && styles.relative,
            draggable && styles.draggable,
            isDragging && styles.isDragging,
          )}
          disabled={disabled}
          onClick={onClickCatchall}
          data-test={dataTest}
        >
          {draggable && <Icon name="drag" className={styles.dragIcon} />}
          {icon && <div className={styles.dropdownItemIcon}>{icon}</div>}
          <div className={styles.dropdownItemText}>{children}</div>

          {checkboxState && (
            <div
              className={cx(
                styles.dropdownItemCheckbox,
                checkboxState === CheckboxState.Unchecked
                  ? styles.unchecked
                  : styles.checked,
                disabled && styles.disabled,
              )}
            >
              {match(checkboxState, {
                [CheckboxState.Checked]: () => <Icon name="checkbox" />,
                [CheckboxState.Unchecked]: () => <Icon name="checkbox-blank" />,
                [CheckboxState.Indeterminate]: () => (
                  <Icon name="checkbox-indeterminate" />
                ),
              })}
            </div>
          )}

          {checkmark && active && (
            <div className={styles.dropdownItemCheckmark}>
              <Icon auto name="tick" />
            </div>
          )}
          {shortcut && (
            <div className={styles.dropdownItemShortcut}>{shortcut}</div>
          )}
        </button>
      </li>
    )
  },
)

interface DropdownItemTitleProps
  extends React.HTMLAttributes<HTMLParagraphElement> {}

export const DropdownItemTitle = ({ ...rest }: DropdownItemTitleProps) => {
  return <p className={styles.dropdownItemTitle} {...rest} />
}

interface DropdownItemDescriptionProps
  extends React.HTMLAttributes<HTMLParagraphElement> {}

export const DropdownItemDescription = ({
  ...rest
}: DropdownItemDescriptionProps) => {
  return <p className={styles.dropdownItemDescription} {...rest} />
}

interface TitleProps {
  children: React.ReactNode
}

// A container for titling sections within the dropdown
export const DropdownTitle = ({ children }: TitleProps) => (
  <div className={styles.dropdownTitle}>
    <p className={styles.dropdownTitleText}>{children}</p>
  </div>
)

export const DropdownDivider = () => (
  <div className={styles.dropdownDivider}>
    <hr />
  </div>
)

interface ContentProps {
  children: React.ReactNode
  autoHeightSize?: boolean
  [key: string]: unknown
}

export const DropdownScroll = React.forwardRef(
  (
    { children, autoHeightSize = false, className: _, ...rest }: ContentProps,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const sizeRef = React.useRef<HTMLDivElement>(null)
    const size = useElementSize(sizeRef)
    const viewPortSize = useViewportSize()

    const isLong = size.height >= 320
    const isModalSheet = viewPortSize.width <= 500
    const screenHeight = viewPortSize.height

    const maxHeight = React.useMemo(() => {
      // Dropdown gets turned into a modal sheet on smaller screens, so we don't want to set a max height.
      if (isModalSheet) {
        return 'none'
      }

      if (autoHeightSize) {
        const delta = screenHeight <= 568 ? 2 : 4

        const calculatedMaxHeight =
          Math.floor((screenHeight - 168) / 50) * 50 + 50 / delta

        const maxHeight = Math.max(calculatedMaxHeight, 40)
        return `${maxHeight}px`
      }

      return '320px'
    }, [autoHeightSize, isModalSheet, screenHeight])

    const dropdownGradientHeight = React.useMemo(() => {
      // Dropdown gets turned into a modal sheet on smaller screens, so we don't want to show the gradient.
      if (isModalSheet) {
        return 0
      }

      if (autoHeightSize) {
        return '20px'
      }

      return '15px'
    }, [isModalSheet, screenHeight])

    return (
      <div
        className={cx(styles.dropdownScroll, isLong && styles.scrollbarPadding)}
        {...rest}
      >
        <div
          ref={mergeRefs([ref, sizeRef])}
          className={cx(styles.dropdownScrollZone)}
          style={{ maxHeight }}
        >
          {children}
          {(isLong || autoHeightSize) && (
            <div
              className={cx(styles.dropdownGradient)}
              style={{ height: dropdownGradientHeight }}
            />
          )}
        </div>
      </div>
    )
  },
)
